import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useGetMessagesQuery,
  useLazyGetMessagesQuery,
  MESSAGES_PAGE_SIZE,
} from "@store/messageApi";
import { Message } from "@models/message";

export interface UsePaginatedMessagesResult {
  messages: Message[];
  isInitialLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  loadNextPage: () => void;
  error: unknown;
}

function sessionKey(patientId: string | undefined, sessionId: string | undefined): string {
  return `${patientId ?? ""}-${sessionId ?? ""}`;
}

/**
 * Fetches messages for a session with pagination. First page is loaded via
 * useGetMessagesQuery; additional pages are loaded on demand via loadNextPage.
 * Resets when patientId or sessionId changes.
 */
export function usePaginatedMessages(
  patientId: string | undefined,
  sessionId: string | undefined
): UsePaginatedMessagesResult {
  const [additionalPages, setAdditionalPages] = useState<Message[][]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [nextPageNumber, setNextPageNumber] = useState(2);
  const sessionKeyRef = useRef(sessionKey(patientId, sessionId));

  const { data: firstPageData, isLoading: isInitialLoading, error } = useGetMessagesQuery(
    {
      patientId: patientId!,
      sessionId,
      page: 1,
      pageSize: MESSAGES_PAGE_SIZE,
    },
    { skip: !patientId || !sessionId }
  );

  const [fetchNextPage, { isLoading: isFetchingMore, data: nextPageData }] =
    useLazyGetMessagesQuery();

  const loadNextPage = useCallback(() => {
    if (!patientId || !sessionId || !hasMore || isFetchingMore) return;
    fetchNextPage({
      patientId,
      sessionId,
      page: nextPageNumber,
      pageSize: MESSAGES_PAGE_SIZE,
    });
  }, [patientId, sessionId, hasMore, isFetchingMore, nextPageNumber, fetchNextPage]);

  // Reset when session or patient changes
  useEffect(() => {
    sessionKeyRef.current = sessionKey(patientId, sessionId);
    setAdditionalPages([]);
    setNextPageNumber(2);
    setHasMore(true);
  }, [sessionId, patientId]);

  // Derive hasMore from first page meta
  useEffect(() => {
    if (firstPageData?.meta) {
      setHasMore(firstPageData.meta.hasNextPage ?? false);
    }
  }, [firstPageData?.meta]);

  // Append next page when lazy fetch completes (ignore if session changed)
  useEffect(() => {
    if (!nextPageData?.data) return;
    const key = sessionKey(patientId, sessionId);
    if (sessionKeyRef.current !== key) return;
    setAdditionalPages((prev) => [...prev, nextPageData.data]);
    setNextPageNumber((n) => n + 1);
    if (nextPageData.meta) {
      setHasMore(nextPageData.meta.hasNextPage ?? false);
    }
  }, [nextPageData, patientId, sessionId]);

  const messages = useMemo(() => {
    const first = firstPageData?.data ?? [];
    return first.concat(...additionalPages);
  }, [firstPageData?.data, additionalPages]);

  return {
    messages,
    isInitialLoading: !!isInitialLoading,
    isFetchingMore: !!isFetchingMore,
    hasMore,
    loadNextPage,
    error,
  };
}
